<template>
  <div id="map" />
  <div class="asset-map-controls">
    <div class="asset-map-controls__zoom">
      <button
        class="asset-map-controls__btn asset-map-controls__btn--top"
        :title="$t('components.map.controls.zoomIn')"
        @click="zoomIn"
      >
        <i class="bi bi-plus-lg" />
      </button>
      <button
        class="asset-map-controls__btn"
        :title="$t('components.map.controls.zoomOut')"
        @click="zoomOut"
      >
        <i class="bi bi-dash-lg" />
      </button>
    </div>

    <button
      v-if="locationCards.length > 0"
      class="asset-map-controls__center"
      :title="$t('components.assetsMap.controls.fitAll')"
      @click="fitAllAssets"
    >
      <i class="bi bi-arrows-fullscreen" />
    </button>

    <div
      class="asset-map-controls__settings"
      :class="{ 'is-open': showMapStyleMenu }"
    >
      <button
        class="asset-map-controls__settings-toggle"
        :class="{ 'asset-map-controls__settings-toggle--active': showMapStyleMenu }"
        :title="$t('components.map.controls.settings')"
        @click="showMapStyleMenu = !showMapStyleMenu"
      >
        <i class="bi bi-sliders" />
      </button>
      <div class="asset-map-controls__settings-menu">
        <div class="asset-map-controls__settings-title">
          {{ $t('components.map.controls.baseMap') }}
        </div>
        <div class="asset-map-controls__settings-styles">
          <button
            v-for="p in availableProviders"
            :key="p.id"
            class="asset-map-controls__settings-style"
            :class="{ 'is-active': selectedProvider === p.id }"
            @click="selectProvider(p.id)"
          >
            <span
              class="asset-map-controls__settings-style-preview"
              :style="{ background: p.preview }"
            />
            <span>{{ p.label }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    class="location-cards-panel"
    :class="{ 'location-cards-panel--collapsed': !showCardsPanel }"
  >
    <button
      class="location-cards-panel__toggle"
      @click="toggleCardsPanel"
      :title="showCardsPanel ? $t('components.assetsMap.list.hideList') : $t('components.assetsMap.list.showList')"
    >
      <i
        class="bi"
        :class="showCardsPanel ? 'bi-chevron-left' : 'bi-geo-alt-fill'"
      />
      <span v-if="!showCardsPanel && locationCards.length">{{ locationCards.length }}</span>
    </button>

    <div
      v-if="showCardsPanel"
      class="location-cards-panel__body"
    >
      <div
        class="location-cards-panel__header"
        @click="toggleCardsPanel"
      >
        <i class="bi bi-geo-alt-fill" />
        <span>{{ $t('components.assetsMap.list.title') }}</span>
        <span class="location-cards-panel__count">{{ locationCards.length }}</span>
        <i class="bi bi-chevron-left location-cards-panel__collapse-icon" />
      </div>

      <div class="location-cards-panel__list">
        <div
          v-if="!cardsLoaded"
          class="location-cards-panel__loading"
        >
          <span
            class="spinner-border location-cards-panel__spinner"
            role="status"
          />
        </div>

        <template v-else>
          <div
            v-for="card in locationCards"
            :key="card.key"
            class="location-card"
            :class="{ 'location-card--active': hoveredCardKey === card.key }"
            @mouseenter="hoveredCardKey = card.key"
            @mouseleave="hoveredCardKey = null"
            @click="onCardClick(card)"
          >
            <div class="location-card__icon">
              <i class="bi" :class="card.icon" />
            </div>
            <div class="location-card__content">
              <div class="location-card__title-row">
                <h4 class="location-card__title">
                  {{ card.label || `${card.lat.toFixed(4)}, ${card.lng.toFixed(4)}` }}
                </h4>
                <span
                  v-if="card.type"
                  class="location-card__type-badge"
                  :class="`location-card__type-badge--${card.type}`"
                >
                  {{ $t('components.wirepasLocations.locationTypes.' + card.type) }}
                </span>
              </div>
              <p
                v-if="card.address"
                class="location-card__address"
              >
                {{ card.address }}
              </p>
              <p class="location-card__subtitle">
                {{ card.count }} {{ deviceCountLabel(card.count) }}
              </p>
            </div>
            <div class="location-card__badge">
              {{ card.count }}
            </div>
          </div>

          <div
            v-if="locationCards.length === 0"
            class="location-cards-panel__empty"
          >
            <i class="bi bi-geo" />
            <p>{{ $t('components.assetsMap.list.empty') }}</p>
          </div>
        </template>
      </div>
    </div>
  </div>

  <!-- Assets Modal -->
  <Transition name="modal">
    <div
      v-if="showModal"
      class="modal-overlay"
      @click="closeModal"
    >
      <div
        class="assets-modal"
        @click.stop
      >
        <div class="assets-modal__header">
          <div class="header-content">
            <div class="header-icon">
              <i class="bi bi-geo-alt-fill" />
            </div>
            <div class="header-text">
              <h3>{{ $t('components.assetsMap.modal.title') }}</h3>
              <p>{{ modalAssets.length }} {{ deviceCountLabel(modalAssets.length) }}</p>
            </div>
          </div>
          <button
            @click="closeModal"
            class="close-btn"
          >
            <i class="bi bi-x" />
          </button>
        </div>
        <div class="assets-modal__content">
          <div class="filter-section">
            <div class="filter-input-wrapper">
              <i class="bi bi-search" />
              <input
                v-model="filterText"
                type="text"
                :placeholder="$t('components.assetsMap.modal.searchPlaceholder')"
                class="filter-input"
              >
              <i
                v-if="filterText"
                @click="filterText = ''"
                class="bi bi-x-circle clear-icon"
              />
            </div>
          </div>

          <div
            v-if="filteredModalAssets.length === 0"
            class="no-results"
          >
            <i class="bi bi-search" />
            <p>{{ $t('components.assetsMap.modal.noResults') }}</p>
          </div>

          <div
            v-for="(asset, index) in filteredModalAssets"
            :key="asset.id"
            class="asset-card"
            :style="{ animationDelay: `${index * 0.06}s` }"
            @click="goToAsset(asset.id)"
          >
            <div class="asset-card__icon-wrapper">
              <div class="asset-card__icon">
                <i class="bi" :class="getAssetIcon(asset)" />
              </div>
            </div>
            <div class="asset-card__content">
              <h4 class="asset-card__name">
                {{ asset.name || asset.serial_number || $t('components.assetsMap.modal.unknownAsset') }}
              </h4>
              <div class="asset-card__params">
                <span class="param-chip">
                  <i class="bi bi-thermometer-half" />
                  {{ getTemperature(asset) }}
                </span>
                <span
                  v-if="getHumidity(asset)"
                  class="param-chip"
                >
                  <i class="bi bi-droplet-half" />
                  {{ getHumidity(asset) }}
                </span>
                <span
                  v-if="getBatteryStatus(asset) !== null"
                  class="param-chip"
                  :class="getBatteryStatusClass(asset)"
                >
                  <i class="bi bi-battery-charging" />
                  {{ getBatteryStatus(asset) }}
                </span>
              </div>
            </div>
            <div class="asset-card__arrow">
              <i class="bi bi-chevron-right" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import 'maplibre-gl/dist/maplibre-gl.css'
import {defineComponent, markRaw} from 'vue'
import maplibregl from 'maplibre-gl'
import { getAssetsWithLatestParams } from '@/crud/asset/getters'
import { getParameterFromToken } from '@/crud/getters'
import { getWirepasLocations } from '@/crud/wirepas-location/getters'
import { fetchWirepasStatus, hasWirepas } from '@/stores/wirepasStore'
import { createMapStyle, DEFAULT_CENTER, isProviderAvailable, MapProvider, resolveProvider, transformMapRequest } from '@/services/map/map-style'
import { isWirepasBatteryLow } from '@/utils/wirepas'
import { LocationClusterManager } from '@/services/map/location-cluster'

export default defineComponent({
  name: 'AssetsMap',
  data() {
    return {
      refreshInterval: null,
      isFirstLoad: true,
      cardsLoaded: false,
      center: DEFAULT_CENTER,
      zoom: 8,
      minZoom: 3,
      maxZoom: 18,
      map: null as maplibregl.Map | null,
      allMarkersVisible: true,
      isUpdating: false,
      isZooming: false,
      showModal: false,
      modalAssets: [] as Array<any>,
      filterText: '',
      locationCards: [] as Array<{ key: string; lat: number; lng: number; count: number; label: string; address: string | null; icon: string; type: string | null; assets: Array<any> }>,
      hoveredCardKey: null as string | null,
      showCardsPanel: true,
      // Wirepas-gateway-linked asset ids, used to route to the Wirepas-specific detail page.
      wirepasAssetIds: new Set<number>(),
      // Per-asset Wirepas location data (name/address/type), kept around so the assets
      // modal can still show the right icon after the map-building closure has finished.
      wirepasCoordsByAssetId: {} as Record<number, { lat: number; lng: number; name: string | null; address: string | null; type: string | null }>,
      // Groups nearby location markers into a count bubble when zoomed out. Not reactive
      // (markRaw) -- it owns its own maplibregl.Marker instances outside Vue's tracking.
      locationClusterManager: null as LocationClusterManager | null,
      showMapStyleMenu: false,
      selectedProvider: 'OFM' as MapProvider,
      providers: [
        { id: 'OFM', label: 'OpenFreeMap', preview: '#eef0f2' },
        { id: 'OSM', label: 'OpenStreetMap', preview: '#cfe3b8' },
        { id: 'HERE', label: 'HERE', preview: '#4a6fa5' },
        { id: 'PTV', label: 'PTV', preview: '#1a9e8f' },
        { id: 'WEBFLEET', label: 'WebFleet', preview: '#e04e2a' },
        { id: 'GOOGLE', label: 'Google', preview: '#aecbf0' }
      ] as Array<{ id: MapProvider; label: string; preview: string }>
    }
  },
  watch: {
    hoveredCardKey(newKey: string | null, oldKey: string | null) {
      const self: any = this;
      self.locationClusterManager?.setHighlighted(oldKey, false);
      self.locationClusterManager?.setHighlighted(newKey, true);
    }
  },
  computed: {
    // A keyless provider would just return 401, so exclude it from the switcher.
    availableProviders(): Array<{ id: MapProvider; label: string; preview: string }> {
      return this.providers.filter((p) => isProviderAvailable(p.id));
    },

    filteredModalAssets() {
      let assets = [...this.modalAssets];

      // Filter by search term
      if (this.filterText.trim()) {
        const searchTerm = this.filterText.toLowerCase().trim();
        assets = assets.filter((asset: any) => {
          const name = (asset.name || asset.serial_number || '').toLowerCase();
          const temperature = this.getTemperature(asset).toLowerCase();
          return name.includes(searchTerm) || temperature.includes(searchTerm);
        });
      }

      // Sort alphabetically by name (A to Z)
      assets.sort((a: any, b: any) => {
        const nameA = (a.name || a.serial_number || '').toLowerCase();
        const nameB = (b.name || b.serial_number || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      return assets;
    }
  },
  methods: {
    openModal(assets: Array<any>) {
      this.modalAssets = assets;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.modalAssets = [];
      this.filterText = '';
    },

    goToAsset(assetId: number | string) {
      this.closeModal();
      this.goToAssetDetails(assetId);
    },

    // Wirepas-linked assets get their own detail page instead of the generic AssetDetails one.
    goToAssetDetails(assetId: number | string) {
      if (typeof assetId === 'number' && this.wirepasAssetIds.has(assetId)) {
        this.$router.push(`/wirepas-assets/${assetId}`);
        return;
      }

      const query = typeof assetId === 'string' ? { source: 'device' } : undefined;
      this.$router.push({ name: 'AssetDetails', params: { id: assetId }, query });
    },

    computeLocationLabel(assets: Array<any>, wirepasCoordsByAssetId?: Record<number, { name: string | null }>): string | null {
      if (wirepasCoordsByAssetId) {
        const withWirepasName = assets.find((a: any) => wirepasCoordsByAssetId[a.id]?.name);
        if (withWirepasName) {
          return wirepasCoordsByAssetId[withWirepasName.id].name;
        }
      }

      const withDescription = assets.find((a: any) => a.location_description && String(a.location_description).trim());
      if (withDescription) {
        return withDescription.location_description;
      }

      // Fallback: most specific asset-group name (e.g. "SA - Automaty 1"), covering
      // regular (non-Wirepas) assets that have no manually-entered location description.
      const withGroupName = assets.find((a: any) => Array.isArray(a.group_names) && a.group_names.length > 0);
      return withGroupName ? withGroupName.group_names[0] : null;
    },

    // Smaller subtitle under the card title: "city, street, countryCode" from
    // asset_group_location — Wirepas location first, then the asset-group's own address.
    computeLocationAddress(assets: Array<any>, wirepasCoordsByAssetId?: Record<number, { address: string | null }>): string | null {
      if (wirepasCoordsByAssetId) {
        const withWirepasAddress = assets.find((a: any) => wirepasCoordsByAssetId[a.id]?.address);
        if (withWirepasAddress) {
          return wirepasCoordsByAssetId[withWirepasAddress.id].address;
        }
      }

      const withGroupAddress = assets.find((a: any) => a.group_address && String(a.group_address).trim());
      return withGroupAddress ? withGroupAddress.group_address : null;
    },

    // Wirepas location type (see WirepasAssetService::ALLOWED_LOCATION_TYPES) -> marker icon.
    wirepasTypeIcon(type: string | null | undefined): string {
      const ICONS: Record<string, string> = { sklep: 'bi-shop', hala: 'bi-building', crossdock: 'bi-truck' }
      return ICONS[type ?? ''] ?? 'bi-box-seam'
    },

    // Icon for a location card/marker: reflects the Wirepas location's type when the
    // cluster has any Wirepas assets, otherwise the generic temperature-cargo icon.
    computeLocationIcon(assets: Array<any>, wirepasCoordsByAssetId?: Record<number, { type?: string | null }>): string {
      if (wirepasCoordsByAssetId) {
        const wirepasAsset = assets.find((a: any) => wirepasCoordsByAssetId[a.id]);
        if (wirepasAsset) {
          return this.wirepasTypeIcon(wirepasCoordsByAssetId[wirepasAsset.id]?.type);
        }
      }
      return 'bi-thermometer-snow';
    },

    // Raw Wirepas location type (sklep/hala/crossdock) for the type badge, or null for a
    // non-Wirepas location card.
    computeLocationType(assets: Array<any>, wirepasCoordsByAssetId?: Record<number, { type?: string | null }>): string | null {
      if (wirepasCoordsByAssetId) {
        const wirepasAsset = assets.find((a: any) => wirepasCoordsByAssetId[a.id]);
        if (wirepasAsset) {
          return wirepasCoordsByAssetId[wirepasAsset.id]?.type ?? null;
        }
      }
      return null;
    },

    // A single asset card always shows a plain box for Wirepas (the shop/hall/crossdock
    // icon is a location-level thing -- shown on the marker and location card instead).
    getAssetIcon(asset: any): string {
      const self: any = this;
      return self.wirepasCoordsByAssetId[asset.id] ? 'bi-box-seam' : 'bi-thermometer-snow';
    },

    // Card click zooms to the location; opening the asset list is the marker's job.
    onCardClick(card: any) {
      const self: any = this;
      if (!self.map) {
        return;
      }
      self.map.flyTo({ center: [card.lng, card.lat], zoom: self.maxZoom });
    },

    fitAllAssets() {
      const self: any = this;
      if (!self.map || self.locationCards.length === 0) {
        return;
      }

      const points: Array<[number, number]> = self.locationCards.map((c: any) => [c.lng, c.lat]);
      const bounds = points.reduce(
        (acc: any, coords: [number, number]) => acc.extend(coords),
        new maplibregl.LngLatBounds(points[0], points[0])
      );
      self.map.fitBounds(bounds, { padding: 48, maxZoom: self.maxZoom });
    },

    zoomIn() {
      (this as any).map?.zoomIn();
    },

    zoomOut() {
      (this as any).map?.zoomOut();
    },

    toggleCardsPanel() {
      this.showCardsPanel = !this.showCardsPanel;
      try {
        localStorage.setItem('atv2_asset_map_cards', this.showCardsPanel ? '1' : '0');
      } catch {
        // ignore storage errors (private mode, quota)
      }
    },

    deviceCountLabel(count: number): string {
      if (count === 1) return this.$t('components.assetsMap.deviceCount.one')
      if (count < 5) return this.$t('components.assetsMap.deviceCount.few')
      return this.$t('components.assetsMap.deviceCount.many')
    },
    getTemperature(asset: any) {
      const tempParam = asset.latest_params && asset.latest_params.find((p: any) =>
        p.can_params_io && p.can_params_io.param_name === 'TL_EYE_TEMPERATURE'
      );
      return tempParam && tempParam.value !== null && tempParam.value !== undefined
        ? parseFloat(tempParam.value).toFixed(1) + '°C'
        : 'N/A';
    },
    getParamValue(asset: any, paramName: string) {
      if (!asset.latest_params || !asset.latest_params.length) return null;
      const param = asset.latest_params.find((p: any) =>
        p.can_params_io && p.can_params_io.param_name === paramName
      );
      return param?.value !== null && param?.value !== undefined ? param.value : null;
    },
    getHumidity(asset: any) {
      const value = this.getParamValue(asset, 'TL_EYE_HUMIDITY');
      return value !== null ? parseFloat(value).toFixed(1) + ' %' : null;
    },
    getBatteryStatus(asset: any) {
      const value = this.getParamValue(asset, 'TL_EYE_LOW_BATTERY');
      if (value !== null) {
        return value === '0' || value === 0 || value === false
          ? this.$t('common.ok')
          : this.$t('common.bad');
      }
      // Wirepas devices don't report TL_EYE_LOW_BATTERY, only the raw voltage —
      // derive ok/bad the same way the backend does (WirepasAssetService::LOW_BATT_THRESHOLD_MV).
      const voltage = this.getParamValue(asset, 'TL_EYE_BATTERY_VOLTAGE');
      if (voltage === null) return null;
      return isWirepasBatteryLow(parseFloat(voltage))
        ? this.$t('common.bad')
        : this.$t('common.ok');
    },
    getBatteryStatusClass(asset: any) {
      const value = this.getParamValue(asset, 'TL_EYE_LOW_BATTERY');
      if (value !== null) {
        return value === '0' || value === 0 || value === false
          ? 'status-ok'
          : 'status-low';
      }
      const voltage = this.getParamValue(asset, 'TL_EYE_BATTERY_VOLTAGE');
      if (voltage === null) return '';
      return isWirepasBatteryLow(parseFloat(voltage))
        ? 'status-low'
        : 'status-ok';
    },
    async createMap(provider: MapProvider) {
      const self: any = this;
      const style = await createMapStyle(provider);

      self.map = new maplibregl.Map({
        container: 'map',
        style,
        center: self.center,
        zoom: self.zoom,
        minZoom: self.minZoom,
        maxZoom: self.maxZoom,
        transformRequest: transformMapRequest,
        canvasContextAttributes: { preserveDrawingBuffer: true }
      });

      const clusterManager = markRaw(new LocationClusterManager(self.map, {
        onClick: (key: string) => {
          const card = self.locationCards.find((c: any) => c.key === key);
          if (card) self.openModal(card.assets);
        },
        onHoverChange: (key: string, entering: boolean) => {
          if (entering) {
            self.hoveredCardKey = key;
          } else if (self.hoveredCardKey === key) {
            // Only clear if nothing newer took over first (mouse moved straight onto another marker).
            self.hoveredCardKey = null;
          }
        }
      }));
      clusterManager.init();
      self.locationClusterManager = clusterManager;

      self.map.on('load', () => {
        self.addVehiclesToMap();
      });

      // With the OFM vector style, 'load' can lag well behind construction — populate immediately.
      self.addVehiclesToMap();

      // Bound to this map instance, so re-register on every createMap() (incl. provider switches).
      setTimeout(() => {
        if (self.map && self.map.on) {
          self.map.on('zoomstart', () => {
            self.isZooming = true;
          });

          self.map.on('zoomend', () => {
            setTimeout(() => {
              self.isZooming = false;
            }, 300);
          });

          self.map.on('movestart', () => {
            self.isZooming = true;
          });

          self.map.on('moveend', () => {
            setTimeout(() => {
              self.isZooming = false;
            }, 300);
          });
        }
      }, 500);
    },

    async changeProvider(provider: MapProvider) {
      const self: any = this;
      if (!self.map || self.selectedProvider === provider) {
        return;
      }

      self.locationClusterManager?.destroy();
      self.locationClusterManager = null;

      try {
        self.map.remove();
      } catch {
        // ignore teardown errors
      }

      self.locationCards = [];
      self.isFirstLoad = true;
      self.cardsLoaded = false;
      await self.createMap(provider);
    },

    selectProvider(p: MapProvider) {
      this.changeProvider(p);
      this.selectedProvider = p;
      this.showMapStyleMenu = false;
    },

    addVehiclesToMap() {
      const self: any = this;
      const validBounds: Array<[number, number]> = [];

      // Prevent concurrent updates or updates during zoom
      if (self.isUpdating) {
        return;
      }

      if (self.isZooming) {
        return;
      }

      // Not gated on loaded()/isStyleLoaded(): with the OFM vector style, background tile
      // requests keep those flags from ever settling, which would starve marker updates.
      if (!self.map) {
        return;
      }

      self.isUpdating = true;

      // Wirepas tags have no GPS; their position comes from the gateway's location instead,
      // used as a fallback in getCoords below. Uses fetchWirepasStatus()'s own resolved value
      // (not the shared hasWirepas.value ref) so a stale/failed check from another component
      // (e.g. the sidebar Menu, which calls the same memoized fetch) can't suppress this.
      const wirepasLocationsFetch = fetchWirepasStatus()
        .then((has) => (has ? getWirepasLocations() : { data: { data: [] } }))
        .catch(() => ({ data: { data: [] } }));

      Promise.all([getAssetsWithLatestParams(), wirepasLocationsFetch])
          .then(([response, wirepasResponse]) => {
            const data = Array.isArray(response.data.data) ? response.data.data : [];
            const wirepasLocations = Array.isArray(wirepasResponse.data.data) ? wirepasResponse.data.data : [];

            self.wirepasAssetIds = new Set<number>(
              wirepasLocations.flatMap((loc: any) => (loc.assets || []).map((a: any) => a.id))
            );

            const wirepasCoordsByAssetId: Record<number, { lat: number; lng: number; name: string | null; address: string | null; type: string | null }> = {};
            wirepasLocations.forEach((loc: any) => {
              const lat = parseFloat(loc.lat);
              const lng = parseFloat(loc.lng);
              if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
                return;
              }
              const address = [loc.address?.city, loc.address?.street, loc.address?.countryCode]
                .filter((part) => part && String(part).trim())
                .join(', ') || null;
              (loc.assets || []).forEach((a: any) => {
                wirepasCoordsByAssetId[a.id] = { lat, lng, name: loc.name || null, address, type: loc.type ?? null };
              });
            });
            self.wirepasCoordsByAssetId = wirepasCoordsByAssetId;

            // with-latest-params hides ungrouped assets from non-ROLE_BOSS users; synthesize a
            // minimal entry so ungrouped Wirepas assets still get picked up below via getCoords.
            const presentAssetIds = new Set(data.map((a: any) => a.id));
            wirepasLocations.forEach((loc: any) => {
              (loc.assets || []).forEach((a: any) => {
                if (presentAssetIds.has(a.id)) {
                  return;
                }
                presentAssetIds.add(a.id);
                data.push({
                  id: a.id,
                  name: a.name,
                  serial_number: a.serialNumber,
                  latest_params: a.latest_params || [],
                  default_latitude: null,
                  default_longitude: null,
                  location_description: null
                });
              });
            });

            const deviceAttachMaxDistanceMeters = 100;
            const locationGroups = {};

            const getCoords = (obj) => {
              const firstParam = obj.latest_params && obj.latest_params.length > 0 ? obj.latest_params[0] : null;
              let lat = firstParam ? parseFloat(firstParam.latitude) : null;
              let lng = firstParam ? parseFloat(firstParam.longitude) : null;

              if ((!lat || !lng || isNaN(lat) || isNaN(lng)) && obj.default_latitude && obj.default_longitude) {
                lat = parseFloat(obj.default_latitude);
                lng = parseFloat(obj.default_longitude);
              }

              if ((!lat || !lng || isNaN(lat) || isNaN(lng)) && wirepasCoordsByAssetId[obj.id]) {
                lat = wirepasCoordsByAssetId[obj.id].lat;
                lng = wirepasCoordsByAssetId[obj.id].lng;
              }

              if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
                return null;
              }

              return { lat, lng };
            };

            const isDevice = (obj) => typeof obj.id === 'string';
            const toRad = (value) => value * Math.PI / 180;
            const distanceMeters = (a, b) => {
              const earthRadius = 6371000;
              const dLat = toRad(b.lat - a.lat);
              const dLng = toRad(b.lng - a.lng);
              const lat1 = toRad(a.lat);
              const lat2 = toRad(b.lat);
              const sinLat = Math.sin(dLat / 2);
              const sinLng = Math.sin(dLng / 2);
              const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
              return 2 * earthRadius * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
            };

            const findNearestGroupKey = (coords): string | null => {
              let closestKey: string | null = null;
              let closestDistance = Number.POSITIVE_INFINITY;
              Object.keys(locationGroups).forEach((key) => {
                const group = locationGroups[key];
                const dist = distanceMeters(coords, { lat: group.lat, lng: group.lng });
                if (dist < closestDistance) {
                  closestDistance = dist;
                  closestKey = key;
                }
              });

              if (closestKey && closestDistance <= deviceAttachMaxDistanceMeters) {
                return closestKey;
              }

              return null;
            };

            // First pass: group assets by location
            data.forEach((obj) => {
              if (isDevice(obj)) {
                return;
              }

              const coords = getCoords(obj);
              if (!coords) {
                return;
              }

              const locKey = `${coords.lat.toFixed(3)}_${coords.lng.toFixed(3)}`;
              if (!locationGroups[locKey]) {
                locationGroups[locKey] = {
                  lat: coords.lat,
                  lng: coords.lng,
                  assets: []
                };
              }
              locationGroups[locKey].assets.push(obj);
            });

            // Second pass: attach devices to nearest asset group or fallback to location
            data.forEach((obj) => {
              if (!isDevice(obj)) {
                return;
              }

              const coords = getCoords(obj);
              if (!coords) {
                return;
              }

              let groupKey: string | null = findNearestGroupKey(coords);
              if (!groupKey) {
                groupKey = `${coords.lat.toFixed(3)}_${coords.lng.toFixed(3)}`;
              }

              if (!locationGroups[groupKey]) {
                locationGroups[groupKey] = {
                  lat: coords.lat,
                  lng: coords.lng,
                  assets: []
                };
              }
              locationGroups[groupKey].assets.push(obj);
            });

            // One card per location; the actual map markers are handed off to
            // locationClusterManager, which merges nearby ones into a count bubble when
            // zoomed out instead of dumping one pin per location on the map.
            const cards: Array<{ key: string; lat: number; lng: number; count: number; label: string; address: string | null; icon: string; type: string | null; assets: Array<any> }> = [];

            Object.keys(locationGroups).forEach((locKey) => {
              const group = locationGroups[locKey];
              const assetsCount = group.assets.length;
              const lat = group.lat;
              const lng = group.lng;
              const icon = self.computeLocationIcon(group.assets, wirepasCoordsByAssetId);
              const type = self.computeLocationType(group.assets, wirepasCoordsByAssetId);

              validBounds.push([lng, lat]);
              cards.push({
                key: locKey,
                lat,
                lng,
                count: assetsCount,
                label: self.computeLocationLabel(group.assets, wirepasCoordsByAssetId),
                address: self.computeLocationAddress(group.assets, wirepasCoordsByAssetId),
                icon,
                type,
                assets: group.assets
              });
            });

            if (self.locationClusterManager) {
              self.locationClusterManager.update(cards.map((c: any) => ({ key: c.key, lat: c.lat, lng: c.lng, count: c.count, icon: c.icon })));
            }

            cards.sort((a, b) => b.count - a.count);
            self.locationCards = cards;

            if (self.isFirstLoad && validBounds.length > 0) {
              const initialBoundsPoint = validBounds[0] as [number, number];
              const bounds = validBounds.reduce(
                (acc, coords) => acc.extend(coords as [number, number]),
                new maplibregl.LngLatBounds(initialBoundsPoint, initialBoundsPoint)
              );
              self.map.fitBounds(bounds, { padding: 48, maxZoom: self.maxZoom });
              self.isFirstLoad = false;
            }

            self.isUpdating = false;
            self.cardsLoaded = true;
          })
          .catch((error) => {
            console.error('Error fetching assets:', error);
            self.isUpdating = false;
            self.cardsLoaded = true;
          });
    }
  },
  beforeUnmount() {
    const self: any = this;

    // Stop auto-refresh immediately
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }

    // Prevent any pending updates
    self.isUpdating = true;
    self.isZooming = true;

    self.locationClusterManager?.destroy();
    self.locationClusterManager = null;

    if (self.map) {
      try {
        self.map.remove();
      } catch (e) {
        // Ignore cleanup errors
      }
    }
  },
  mounted() {
    const self: any = this

    // Kicked off early so hasWirepas is resolved by the time addVehiclesToMap first runs.
    fetchWirepasStatus();

    getParameterFromToken('MAP_TYPE')
        .then((res: string) => {
          const provider = resolveProvider(res);
          self.selectedProvider = provider;
          this.createMap(provider)
        })
        .catch(() => {
          this.createMap('OFM')
        })

    try {
      const stored = localStorage.getItem('atv2_asset_map_cards');
      self.showCardsPanel = stored !== null ? stored === '1' : window.innerWidth > 768;
    } catch {
      self.showCardsPanel = true;
    }

    self.refreshInterval = setInterval(() => {
      self.addVehiclesToMap();
    }, parseInt(process.env.VUE_APP_MAP_INTERVAL_REFRESH));
  }
})
</script>
<style lang="scss" scoped>
@import '@/assets/scss/assets-map.scss';
</style>
